/**
 * NewsletterView — a newsletter's configuration, formerly
 * working/newsletter-config/<slug>.yaml (FEAT-096).
 *
 * S-402: read-only. Reachable only from the gated Newsletter nav item
 * (canSeeNewsletter); the API enforces the same gate.
 * S-403: the settings, deep dives and editorial guidelines are editable
 * (lib/newsletter-form.ts does the parsing and diffing).
 *
 * Newsletters belong to one workspace and the list is RLS-scoped to the
 * active one, so this view syncs the active workspace to the sidebar picker
 * the way CalendarView does — otherwise the list would silently describe a
 * different workspace than the one shown as selected.
 */
import { useState, type ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError, useApi, useApiQuery } from "@/lib/api";
import {
  buildSettingsPatch,
  draftFromConfig,
  type NewsletterSettingsDraft,
  type NewsletterSettingsPatch,
} from "@/lib/newsletter-form";
import {
  buildSourcesPatch,
  emptySourceDraft,
  sourceToDraft,
  type SourceDraft,
} from "@/lib/newsletter-sources-form";
import { useEnsureActiveWorkspace } from "@/lib/useActiveWorkspace";
import { useWorkspaceContext } from "@/lib/workspace-context";
import type {
  NewsletterConfig,
  NewsletterDeepDives,
  NewsletterDistributionEntry,
  NewsletterSource,
  NewsletterSummary,
} from "@/types/newsletter";

export function NewsletterView() {
  const { activeWorkspace, allWorkspaces, isLoading: wsLoading } = useWorkspaceContext();
  const wsId = allWorkspaces ? undefined : activeWorkspace?.id;
  useEnsureActiveWorkspace(wsId);

  const listQ = useApiQuery<NewsletterSummary[]>(["newsletters", wsId ?? 0], "/newsletters", {
    enabled: wsId != null,
  });
  const [pickedId, setPickedId] = useState<number | null>(null);
  const newsletters = listQ.data ?? [];
  const selectedId =
    newsletters.find((n) => n.id === pickedId)?.id ?? newsletters[0]?.id ?? null;

  const detailQ = useApiQuery<NewsletterConfig>(
    ["newsletters", wsId ?? 0, selectedId ?? 0],
    `/newsletters/${selectedId ?? 0}`,
    { enabled: selectedId != null }
  );

  return (
    <main className="main settings-main">
      <div className="settings-page">
        <header className="settings-header">
          <div>
            <h1 className="settings-title">Newsletter</h1>
            <p className="settings-sub">
              What the weekly newsletter run reads: objective, item counts, deep dives, and sources.
            </p>
          </div>
        </header>

        {wsLoading ? (
          <div className="state-loading">Loading…</div>
        ) : allWorkspaces ? (
          <div className="panel-empty">
            Newsletters belong to a single workspace. Pick one in the workspace menu to see its newsletter.
          </div>
        ) : listQ.isLoading ? (
          <div className="state-loading">Loading newsletters…</div>
        ) : listQ.error ? (
          <div className="state-error">Could not load newsletters: {listQ.error.message}</div>
        ) : newsletters.length === 0 ? (
          <div className="panel-empty">
            No newsletter is configured in this workspace. Newsletters belong to a single workspace — switch
            workspace to see another one.
          </div>
        ) : (
          <>
            {newsletters.length > 1 && (
              <div className="team-toolbar">
                <span className="team-toolbar-label">Newsletter</span>
                <select
                  className="settings-select"
                  value={selectedId ?? ""}
                  onChange={(e) => setPickedId(Number(e.target.value))}
                >
                  {newsletters.map((n) => (
                    <option key={n.id} value={n.id}>
                      {n.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {detailQ.isLoading && <div className="state-loading">Loading configuration…</div>}
            {detailQ.error && (
              <div className="state-error">Could not load this newsletter: {detailQ.error.message}</div>
            )}
            {/* key={config.id} — review fix: without it, switching the picker
                while a settings/sources draft is open keeps the stale draft
                mounted against the newly-selected newsletter's id, so Save
                would write one newsletter's edited values onto another's row. */}
            {detailQ.data && <NewsletterConfigPanel key={detailQ.data.id} config={detailQ.data} />}
          </>
        )}
      </div>
    </main>
  );
}

/** A 422's `details`, or the error's own message when it isn't one. */
function saveErrorMessages(err: unknown): string[] {
  if (err instanceof ApiError && err.status === 422) {
    try {
      const body = JSON.parse(err.body) as { details?: string[] };
      if (body.details?.length) return body.details;
    } catch {
      /* not JSON — fall through */
    }
  }
  return [err instanceof Error ? err.message : String(err)];
}

/**
 * S-403 — settings are editable and the next Saturday run reads them from the
 * API. S-404 — sources have their own independent edit/Save, a full-list
 * replace. Distribution stays read-only (plan Decision 4). Editing one
 * section doesn't touch the other's saved state — each has its own draft.
 */
function NewsletterConfigPanel({ config }: { config: NewsletterConfig }) {
  const api = useApi();
  const qc = useQueryClient();

  const [settingsDraft, setSettingsDraft] = useState<NewsletterSettingsDraft | null>(null);
  const [settingsErrors, setSettingsErrors] = useState<string[]>([]);
  const [sourcesDraft, setSourcesDraft] = useState<SourceDraft[] | null>(null);
  const [sourcesErrors, setSourcesErrors] = useState<string[]>([]);

  const invalidate = () => void qc.invalidateQueries({ queryKey: ["newsletters"] });

  const saveSettings = useMutation({
    mutationFn: (patch: NewsletterSettingsPatch) =>
      api<NewsletterConfig>(`/newsletters/${config.id}`, { method: "PATCH", body: JSON.stringify(patch) }),
    onSuccess: () => {
      invalidate();
      setSettingsDraft(null);
      setSettingsErrors([]);
    },
    onError: (err) => setSettingsErrors(saveErrorMessages(err)),
  });

  const saveSources = useMutation({
    mutationFn: (sources: NewsletterSource[]) =>
      api<{ sources: NewsletterSource[] }>(`/newsletters/${config.id}/sources`, {
        method: "PUT",
        body: JSON.stringify({ sources }),
      }),
    onSuccess: () => {
      invalidate();
      setSourcesDraft(null);
      setSourcesErrors([]);
    },
    onError: (err) => setSourcesErrors(saveErrorMessages(err)),
  });

  function submitSettings() {
    if (!settingsDraft) return;
    const result = buildSettingsPatch(config, settingsDraft);
    if (!result.ok) {
      setSettingsErrors(result.errors);
      return;
    }
    if (Object.keys(result.patch).length === 0) {
      setSettingsDraft(null);
      return;
    }
    saveSettings.mutate(result.patch);
  }

  function submitSources() {
    if (!sourcesDraft) return;
    const result = buildSourcesPatch(sourcesDraft);
    if (!result.ok) {
      setSourcesErrors(result.errors);
      return;
    }
    saveSources.mutate(result.sources);
  }

  return (
    <>
      {settingsDraft ? (
        <>
          <SettingsForm draft={settingsDraft} onChange={setSettingsDraft} />
          <ErrorList errors={settingsErrors} />
          <div className="settings-form-actions newsletter-actions">
            <button type="button" className="btn-primary" onClick={submitSettings} disabled={saveSettings.isPending}>
              {saveSettings.isPending ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              className="btn-ghost"
              onClick={() => {
                setSettingsDraft(null);
                setSettingsErrors([]);
              }}
              disabled={saveSettings.isPending}
            >
              Cancel
            </button>
            <span className="amp-rules-header-sub">The next weekly run uses what you save here.</span>
          </div>
        </>
      ) : (
        <>
          <div className="newsletter-actions">
            <button
              type="button"
              className="btn-ghost"
              onClick={() => {
                setSettingsErrors([]);
                setSettingsDraft(draftFromConfig(config));
              }}
              disabled={sourcesDraft != null}
            >
              Edit settings
            </button>
          </div>
          <SettingsReadOnly config={config} />
        </>
      )}

      {sourcesDraft ? (
        <Section title={`Sources (${sourcesDraft.length})`}>
          <SourcesEditor drafts={sourcesDraft} onChange={setSourcesDraft} />
          <ErrorList errors={sourcesErrors} />
          <div className="settings-form-actions newsletter-actions">
            <button type="button" className="btn-primary" onClick={submitSources} disabled={saveSources.isPending}>
              {saveSources.isPending ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              className="btn-ghost"
              onClick={() => {
                setSourcesDraft(null);
                setSourcesErrors([]);
              }}
              disabled={saveSources.isPending}
            >
              Cancel
            </button>
            <span className="amp-rules-header-sub">The next run collects from what you save here.</span>
          </div>
        </Section>
      ) : (
        <Section title={`Sources (${config.sources.length})`}>
          <div className="newsletter-actions">
            <button
              type="button"
              className="btn-ghost"
              onClick={() => {
                setSourcesErrors([]);
                setSourcesDraft(config.sources.map(sourceToDraft));
              }}
              disabled={settingsDraft != null}
            >
              Edit sources
            </button>
          </div>
          <SourcesReadOnly sources={config.sources} />
        </Section>
      )}

      <Section title="Distribution" sub="Not used by the pipeline — shown for reference. Publishing runs through publish targets.">
        <DistributionReadOnly entries={config.distribution} />
      </Section>
    </>
  );
}

function ErrorList({ errors }: { errors: string[] }) {
  if (errors.length === 0) return null;
  return (
    <ul className="settings-field-error newsletter-errors">
      {errors.map((e) => (
        <li key={e}>{e}</li>
      ))}
    </ul>
  );
}

function SettingsForm({
  draft,
  onChange,
}: {
  draft: NewsletterSettingsDraft;
  onChange: (next: NewsletterSettingsDraft) => void;
}) {
  const set = <K extends keyof NewsletterSettingsDraft>(key: K, value: NewsletterSettingsDraft[K]) =>
    onChange({ ...draft, [key]: value });
  const setDd = <K extends keyof NewsletterSettingsDraft["deep_dives"]>(
    key: K,
    value: NewsletterSettingsDraft["deep_dives"][K]
  ) => onChange({ ...draft, deep_dives: { ...draft.deep_dives, [key]: value } });

  return (
    <>
      <Section title="Settings">
        <label className="settings-field">
          <span className="settings-label">Objective</span>
          <textarea
            className="settings-input newsletter-textarea"
            rows={3}
            value={draft.objective}
            onChange={(e) => set("objective", e.target.value)}
          />
        </label>
        <label className="settings-field">
          <span className="settings-label">Output format</span>
          <select
            className="settings-select"
            value={draft.output_format}
            onChange={(e) => set("output_format", e.target.value as "html" | "markdown")}
          >
            <option value="html">HTML</option>
            <option value="markdown">Markdown</option>
          </select>
        </label>
        <div className="newsletter-number-row">
          <NumberInput label="Collection window (days)" value={draft.periodicity_days} onChange={(v) => set("periodicity_days", v)} />
          <NumberInput label="Minimum items" value={draft.min_items} onChange={(v) => set("min_items", v)} />
          <NumberInput label="Maximum items" value={draft.max_items} onChange={(v) => set("max_items", v)} />
        </div>
      </Section>

      <Section title="Deep dives">
        <Checkbox label="Write deep dives" checked={draft.deep_dives.enabled} onChange={(v) => setDd("enabled", v)} />
        <div className="newsletter-number-row">
          <NumberInput label="Deep dives per edition" value={draft.deep_dives.count} onChange={(v) => setDd("count", v)} />
          <NumberInput
            label="Candidates to choose from"
            hint="Leave empty to use the maximum item count."
            value={draft.deep_dives.candidates_count}
            onChange={(v) => setDd("candidates_count", v)}
          />
        </div>
        <Checkbox
          label="Editorial gate — you choose which candidates become deep dives"
          checked={draft.deep_dives.editorial_gate}
          onChange={(v) => setDd("editorial_gate", v)}
        />
        <Checkbox label="Auto-advance" checked={draft.deep_dives.auto_advance} onChange={(v) => setDd("auto_advance", v)} />
      </Section>

      <Section
        title="Editorial guidelines"
        sub="Given to the writer word for word — it defines the edition's segments."
      >
        <textarea
          className="settings-input newsletter-textarea newsletter-body-input"
          rows={16}
          value={draft.body}
          onChange={(e) => set("body", e.target.value)}
        />
      </Section>
    </>
  );
}

function NumberInput({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="settings-field">
      <span className="settings-label">
        {label}
      </span>
      <input
        type="number"
        min={1}
        step={1}
        className="settings-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {hint && <span className="newsletter-field-hint">{hint}</span>}
    </label>
  );
}

function Checkbox({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="newsletter-checkbox">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

function SettingsReadOnly({ config }: { config: NewsletterConfig }) {
  return (
    <>
      <Section title={config.name} sub={config.slug}>
        <Field label="Objective" value={config.objective} multiline />
        <Field label="Output format" value={config.output_format === "html" ? "HTML" : "Markdown"} />
        <Field label="Collection window" value={`Last ${config.periodicity_days} days`} />
        <Field label="Items per edition" value={`${config.min_items}–${config.max_items}`} />
      </Section>

      <Section title="Deep dives">
        <DeepDivesReadOnly deepDives={config.deep_dives} />
      </Section>

      <Section
        title="Editorial guidelines"
        sub="Given to the writer word for word — it defines the edition's segments."
      >
        {config.body ? (
          <pre className="newsletter-body">{config.body}</pre>
        ) : (
          <div className="panel-empty">No editorial guidelines — the writer uses its standard segments.</div>
        )}
      </Section>
    </>
  );
}

function DeepDivesReadOnly({ deepDives: d }: { deepDives: NewsletterDeepDives }) {
  if (!d.enabled) return <Field label="Deep dives" value="Off" />;
  return (
    <>
      <Field label="Deep dives per edition" value={String(d.count)} />
      <Field
        label="Editorial gate"
        value={
          d.editorial_gate
            ? `On — you choose from ${d.candidates_count ?? "the curated"} candidates`
            : "Off — the top items become deep dives automatically"
        }
      />
      <Field label="Auto-advance" value={d.auto_advance ? "On" : "Off"} />
      {d.image_style && <Field label="Image style" value={d.image_style} />}
      {d.image_types && <Field label="Image types" value={d.image_types} />}
      {d.image_count != null && <Field label="Images" value={String(d.image_count)} />}
    </>
  );
}

const SOURCE_TYPE_LABEL: Record<NewsletterSource["type"], string> = {
  blog: "Blog",
  sitemap: "Sitemap",
  podcast: "Podcast",
  youtube: "YouTube",
};

/** The one field a source of this type is actually collected from. */
function sourceFeed(s: NewsletterSource): string {
  if (s.type === "sitemap") return s.sitemap_url ?? "";
  if (s.type === "youtube") return s.channel_id ?? "";
  return s.rss_feed ?? "";
}

const SOURCE_TYPES: NewsletterSource["type"][] = ["blog", "sitemap", "podcast", "youtube"];

/**
 * All 81 sources, add/edit-inline/remove, one Save for the whole list —
 * matching the API's full-list replace (plan's "no per-row CRUD" note).
 * Nothing is sent per row; Save is the only request.
 */
function SourcesEditor({
  drafts,
  onChange,
}: {
  drafts: SourceDraft[];
  onChange: (next: SourceDraft[]) => void;
}) {
  function update(i: number, patch: Partial<SourceDraft>) {
    onChange(drafts.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));
  }
  function remove(i: number) {
    onChange(drafts.filter((_, idx) => idx !== i));
  }
  function add() {
    onChange([...drafts, emptySourceDraft()]);
  }

  return (
    <>
      <table className="workspaces-table newsletter-sources-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Type</th>
            <th>URL</th>
            <th>Feed / ID</th>
            <th className="ws-actions-col">Actions</th>
          </tr>
        </thead>
        <tbody>
          {drafts.map((d, i) => (
            // Same key convention as SourcesReadOnly's own row a few functions
            // down — position alone breaks on remove(), since every later
            // row's index shifts onto a logically different source.
            <tr key={`${i}-${d.name}`} className="workspaces-row">
              <td>
                <input
                  className="settings-input"
                  value={d.name}
                  onChange={(e) => update(i, { name: e.target.value })}
                />
                <span className="newsletter-field-hint">Renaming resets what this source has already collected.</span>
              </td>
              <td>
                <select
                  className="settings-select"
                  value={d.type}
                  onChange={(e) => update(i, { type: e.target.value as NewsletterSource["type"] })}
                >
                  {SOURCE_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {SOURCE_TYPE_LABEL[t]}
                    </option>
                  ))}
                </select>
              </td>
              <td>
                <input className="settings-input" value={d.url} onChange={(e) => update(i, { url: e.target.value })} />
              </td>
              <td>
                {d.type === "sitemap" && (
                  <>
                    <input
                      className="settings-input"
                      placeholder="Sitemap URL"
                      value={d.sitemap_url}
                      onChange={(e) => update(i, { sitemap_url: e.target.value })}
                    />
                    <input
                      className="settings-input"
                      placeholder="URL pattern (optional)"
                      value={d.url_pattern}
                      onChange={(e) => update(i, { url_pattern: e.target.value })}
                    />
                  </>
                )}
                {d.type === "youtube" && (
                  <input
                    className="settings-input"
                    placeholder="Channel ID"
                    value={d.channel_id}
                    onChange={(e) => update(i, { channel_id: e.target.value })}
                  />
                )}
                {(d.type === "blog" || d.type === "podcast") && (
                  <input
                    className="settings-input"
                    placeholder="RSS feed"
                    value={d.rss_feed}
                    onChange={(e) => update(i, { rss_feed: e.target.value })}
                  />
                )}
              </td>
              <td>
                <button type="button" className="btn-ghost btn-danger" onClick={() => remove(i)}>
                  Remove
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button type="button" className="btn-ghost" style={{ marginTop: 8 }} onClick={add}>
        + Add source
      </button>
    </>
  );
}

function SourcesReadOnly({ sources }: { sources: NewsletterSource[] }) {
  if (sources.length === 0) return <div className="panel-empty">No sources.</div>;
  return (
    <table className="workspaces-table">
      <thead>
        <tr>
          <th>Name</th>
          <th>Type</th>
          <th>Collected from</th>
        </tr>
      </thead>
      <tbody>
        {sources.map((s, i) => (
          <tr key={`${i}-${s.name}`} className="workspaces-row">
            <td>
              <a href={s.url} target="_blank" rel="noreferrer">
                {s.name}
              </a>
            </td>
            <td>
              <span className="pill pill-info">{SOURCE_TYPE_LABEL[s.type] ?? s.type}</span>
            </td>
            <td className="newsletter-source-feed">{sourceFeed(s)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function DistributionReadOnly({ entries }: { entries: NewsletterDistributionEntry[] }) {
  if (entries.length === 0) return <div className="panel-empty">None recorded.</div>;
  return (
    <>
      {entries.map((e, i) => (
        <Field
          key={i}
          label={e.type[0].toUpperCase() + e.type.slice(1)}
          value={[e.target, e.audience, e.enabled === false ? "disabled" : null].filter(Boolean).join(" · ")}
        />
      ))}
    </>
  );
}

function Section({ title, sub, children }: { title: string; sub?: string; children: ReactNode }) {
  return (
    <section className="newsletter-section">
      <div className="amp-rules-header">
        <h2>{title}</h2>
        {sub && <span className="amp-rules-header-sub">{sub}</span>}
      </div>
      {children}
    </section>
  );
}

function Field({ label, value, multiline }: { label: string; value: string; multiline?: boolean }) {
  return (
    <div className="newsletter-field">
      <span className="settings-label">{label}</span>
      <span className={multiline ? "newsletter-field-value is-multiline" : "newsletter-field-value"}>
        {value}
      </span>
    </div>
  );
}
